import emoji from 'node-emoji'
import { table } from 'table'
import axios, { AxiosRequestConfig, Method } from 'axios'
import chalk from 'chalk'
import { Answers } from 'inquirer'

import { ExtendedQuestion, ExtendedQuestionMap } from '../../types'
import { Config, Env } from './types'
import { isValidCircleCIProjectSlug } from './validators'
import { isValidBasicStringOfLengthN } from '../validators'

const baseCircleUrl = 'https://circleci.com/api/v2'
const circleEnvUrl = (projectSlug: string) => `${baseCircleUrl}/project/${projectSlug}/envvar`

export class CircleApiUtils {
  private config: Config
  private circleEnvUrl: string

  constructor(config: Config) {
    this.config = config
    this.circleEnvUrl = circleEnvUrl(this.config.projectSlug)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  circleRequest = (
    url: string,
    method: Method,
    params?: any | undefined,
    data?: any | undefined,
  ): AxiosRequestConfig => ({
    method,
    url,
    headers: {
      'Circle-Token': this.config.token,
    },
    params,
    data,
  })

  private getEnvsWithNextPageToken = async (nextPageToken: string | null): Promise<Env[]> => {
    if (!nextPageToken) {
      return []
    }

    const request = this.circleRequest(this.circleEnvUrl, 'GET', { next_page_token: nextPageToken })
    const result = await axios(request)

    return result.data.items.concat(await this.getEnvsWithNextPageToken(result.data.next_page_token))
  }

  getAllEnvs = async (): Promise<Env[]> => {
    const request = this.circleRequest(this.circleEnvUrl, 'GET')
    const result = await axios(request)

    const nextPageToken = result.data.next_page_token
    let nextEnvs: Env[] = []
    if (nextPageToken) {
      nextEnvs = await this.getEnvsWithNextPageToken(nextPageToken)
    }

    return result.data.items.concat(nextEnvs)
  }

  createEnv = async (env: Env): Promise<Env> => {
    const request = this.circleRequest(this.circleEnvUrl, 'POST', null, env)
    const result = await axios(request)

    return result.data
  }

  createEnvs = async (envs: Env[]): Promise<PromiseSettledResult<Env>[]> => {
    return Promise.allSettled(envs.map(this.createEnv))
  }
}

const displayArrayMap = (env: Env, creationResult: PromiseSettledResult<Env>): string[] => [
  env.name,
  creationResult.status === 'fulfilled' ? creationResult.value.value : null,
  creationResult.status === 'fulfilled' ? emoji.get('white_check_mark') : emoji.get('x'),
  creationResult.status === 'rejected' ? creationResult.reason : null,
]

export const displayEnvCreationResults = (envs: Env[], creationResults: PromiseSettledResult<Env>[]): void => {
  const header = ['Name', 'Value', 'Status', 'Reason']
  const displayEnvs = envs.map((env, index): string[] => displayArrayMap(env, creationResults[index]))

  console.log(table([header, ...displayEnvs]))
}

export const displayEnvList = (envs: Env[]): void => {
  const header = ['Name', 'Value']
  const displayEnvs = envs.map(env => [env.name, env.value])

  console.log(table([header, ...displayEnvs]))
}

const projectSlug = process.env.CIRCLE_PROJECT_SLUG
const circleToken = process.env.CIRCLE_TOKEN

export const circleApiQuestions: ExtendedQuestion[] = [
  {
    type: 'input',
    name: 'projectSlug',
    prefix: emoji.get('rocket'),
    message: `Please enter your project slug (<vcs-slug>/<org-name>/<repo-name>) ${chalk.underline.green(
      '(e.g. gh/Nona-Creative/react-native-ci-utils)',
    )}:`,
    ...(projectSlug && { default: projectSlug }),
    validate: isValidCircleCIProjectSlug,
  },
  {
    type: 'input',
    name: 'circleToken',
    prefix: emoji.get('i_love_you_hand_sign'),
    message: `Your CircleCI personal API token (https://app.circleci.com/settings/user/tokens) ${chalk.underline.green(
      '(e.g. d4a6fbc5f57a14bdd64a4406170f61cEXAMPLE)',
    )}:`,
    ...(circleToken && { default: circleToken }),
    validate: isValidBasicStringOfLengthN(1),
  },
]

export const buildEnvQuestions = (selectedEnvs: string[], questionsMap: ExtendedQuestionMap): ExtendedQuestion[] => {
  return selectedEnvs.map(envKey => questionsMap[envKey])
}

export const buildEnvsFromAnswers = (
  selectedEnvs: string[],
  envAnswers: Answers,
  questionsMap: ExtendedQuestionMap,
): Env[] => {
  return selectedEnvs.map(envKey => ({
    name: envKey,
    value: envAnswers[questionsMap[envKey].name as string],
  }))
}
